import { useState, useEffect, useCallback, useRef } from 'react';
import { parseWebsitesResponse } from '../utils/elasticsearchParsers';

const ITEMS_PER_PAGE = 50;
const ES_INDEX = "website_audits";

/**
 * Custom hook for managing websites list data fetching
 * Handles pagination, filtering, and aggregation
 */
export const useWebsitesList = (wsProvider) => {
    const [websites, setWebsites] = useState([]);
    const [totalHits, setTotalHits] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [funnelStatusAggregation, setFunnelStatusAggregation] = useState({});
    const [selectedFunnelStatus, setSelectedFunnelStatus] = useState('__all__');
    const totalAllHitsRef = useRef(0);

    /**
     * Builds Elasticsearch query with optional funnel_status filter
     */
    const buildQuery = useCallback((page, funnelStatus) => {
        const from = (page - 1) * ITEMS_PER_PAGE;
        const size = ITEMS_PER_PAGE;
        
        // Build query with optional funnel_status filter
        const queryFilter = (funnelStatus && funnelStatus !== '__all__')
            ? {
                term: {
                    "funnel_status": funnelStatus
                }
            }
            : { match_all: {} };
        
        return {
            query: queryFilter,
            from: from,
            size: size,
            sort: {
                host: {
                    order: "asc"
                }
            },
            aggs: {
                all_statuses: {
                    global: {},
                    aggs: {
                        funnel_status_agg: {
                            terms: {
                                field: "funnel_status",
                                size: 100
                            }
                        }
                    }
                }
            }
        };
    }, []);

    /**
     * Loads websites from Elasticsearch
     */
    const loadWebsites = useCallback((page = 1, funnelStatus = null) => {
        if (!wsProvider?.ws) {
            console.error('WebSocket provider not available');
            return;
        }

        setLoading(true);
        const query = buildQuery(page, funnelStatus);

        wsProvider.ws.waitForSocketAndSend({
            action: "runESQuery",
            options: {
                project_id: "cms",
                query: query,
                index: ES_INDEX,
            }
        }, (response) => {
            setLoading(false);
            
            const parsed = parseWebsitesResponse(response, funnelStatus, totalAllHitsRef.current);
            
            setWebsites(parsed.websites);
            setTotalHits(parsed.totalHits);
            
            if (parsed.aggregations) {
                setFunnelStatusAggregation(parsed.aggregations);
            } else {
                setWebsites([]);
                setTotalHits(0);
            }
            
            totalAllHitsRef.current = parsed.totalAllHits;
        });
    }, [wsProvider, buildQuery]);

    // Load websites on mount and when page or selectedFunnelStatus changes
    useEffect(() => {
        loadWebsites(currentPage, selectedFunnelStatus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, selectedFunnelStatus]);

    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
    }, []);

    const handleFunnelStatusChange = useCallback((funnelStatus) => {
        setSelectedFunnelStatus(funnelStatus);
        setCurrentPage(1); // Reset to first page when changing tabs
    }, []);

    return {
        websites,
        totalHits,
        currentPage,
        loading,
        funnelStatusAggregation,
        selectedFunnelStatus,
        itemsPerPage: ITEMS_PER_PAGE,
        handlePageChange,
        handleFunnelStatusChange,
        reloadWebsites: loadWebsites
    };
};

